/** 261104 E1-P1 — màn một cổng duyệt (Chọn kết · Cải biên · Khung · Chia tập): khung chung + thanh quyết định + «sửa bằng lời». */
import React, { useRef, useState } from 'react';
import type { ChangesDataView, EndingDataView, EpisodesDataView, SkillChangeChoice, SkillGate, SkillRunProjection, SpineDataView } from '../../../types/skillAdaptation';
import { SKILL_GATES } from '../../../types/skillAdaptation';
import { Alert, DecisionBar, InstructionBox } from './SkillParts';
import { ChangesBody, EndingBody, SpineBody, SplitBody } from './SkillGateBodies';
import {
  GATE_TITLE, applySkipLabel, approveChoiceOf, countApplySkip, initialChoicesOf, isLongContent, milestoneNamesOf, paidActionsDisabled, showInstructionBox, freshIdsOf, gateErrorCopyOf,
} from './skillAdaptUtils';
import { episodesBarCopyOf } from './skillWritingUtils';

export interface GateScreenProps {
  run: SkillRunProjection; gate: SkillGate; stateId: string; viewing: boolean; busy: boolean;
  onApprove: (choice: unknown) => void; onRewrite: (instruction: string, choices?: Record<string, SkillChangeChoice>) => void;
  onRegenerate: () => void; onResplit: () => void; onResume: () => void; onRaiseCap: () => void; onBackToCurrent: () => void;
}

export const SkillGateScreen: React.FC<GateScreenProps> = ({ run, gate, stateId, viewing, busy, onApprove, onRewrite, onRegenerate, onResplit, onResume, onRaiseCap, onBackToCurrent }) => {
  const doc = run.documents[gate].current; const data = (doc?.data ?? null) as unknown;
  const decision = run.decisions.find((d) => d.gate === gate);
  const readOnly = viewing;
  const names = milestoneNamesOf(run);
  const empty = stateId.endsWith('-empty'); const editing = stateId.endsWith('-editing'); const errored = stateId.endsWith('-error'); const revised = stateId.endsWith('-revised'); const keepState = stateId === 'changes-keep';
  const overlong = gate === 'EPISODES' ? run.overlongEpisodes : [];
  const capSec = run.input.limits.maxEpisodeSeconds;
  const paidOff = paidActionsDisabled(run, busy);
  const errCopy = gateErrorCopyOf(run);

  const approvedChoice = (decision?.choice ?? {}) as { optionId?: string; decisions?: Record<string, SkillChangeChoice>; keepOverlong?: number[] };
  const [optionId, setOptionId] = useState<string | null>(() => {
    if (readOnly) return approvedChoice.optionId ?? null;
    const opts = ((data as EndingDataView | null)?.options ?? []); return opts.find((o) => o.recommended)?.id ?? opts[0]?.id ?? null;
  });
  const [choices, setChoices] = useState<Record<string, SkillChangeChoice>>(() => (readOnly ? { ...(approvedChoice.decisions ?? {}) } : initialChoicesOf(data as ChangesDataView | null)));
  const [instruction, setInstruction] = useState<string>(() => (revised && doc?.instruction ? doc.instruction : ''));
  const instrRef = useRef<HTMLTextAreaElement | null>(null);

  const bulk = (mode: 'all' | 'noNew'): void => {
    const rows = (data as ChangesDataView | null)?.changes ?? []; const next: Record<string, SkillChangeChoice> = {};
    for (const c of rows) next[c.id] = mode === 'all' || c.kind !== 'NEW' ? 'APPLY' : 'SKIP';
    setChoices(next);
  };
  const counts = countApplySkip(choices);
  const idx = SKILL_GATES.indexOf(gate);

  let body: React.ReactNode = null;
  if (empty) {
    body = <div className="empty"><h3>Chưa có bản đề xuất</h3><p className="muted">Máy chưa tạo được tài liệu để bạn duyệt.</p><div><button type="button" className="btn primary" disabled={paidOff} onClick={onRegenerate}>Để máy tạo lại phần còn thiếu</button></div></div>;
  } else if (gate === 'ENDING') {
    body = <EndingBody data={data as EndingDataView} text={doc?.text ?? ''} names={names} optionId={optionId} onPick={setOptionId} readOnly={readOnly || editing} />;
  } else if (gate === 'CHANGES') {
    body = <ChangesBody data={data as ChangesDataView} text={doc?.text ?? ''} names={names} choices={choices} readOnly={readOnly || editing} freshIds={freshIdsOf(data as ChangesDataView | null, readOnly)} onChoose={(id, v) => setChoices({ ...choices, [id]: v })} onBulk={bulk} onWantChange={() => instrRef.current?.focus()} />;
  } else if (gate === 'SPINE') {
    body = <SpineBody data={data as SpineDataView} text={doc?.text ?? ''} names={names} />;
  } else {
    body = <SplitBody data={data as EpisodesDataView} text={doc?.text ?? ''} names={names} capSec={capSec} overlong={overlong} kept={approvedChoice.keepOverlong ?? []} disabled={paidOff || readOnly} onResplit={onResplit} />;
  }

  // thanh quyết định
  const noDoc = empty || !doc;
  let barTitle = 'Chọn xong thì duyệt'; let barSub = ''; let barLabel = 'Duyệt & tiếp tục';
  if (gate === 'CHANGES' && !noDoc) barTitle = `${applySkipLabel(counts)} thay đổi`;
  const epBar = episodesBarCopyOf(run, overlong.length);
  if (gate === 'EPISODES') { barTitle = epBar.title; barSub = epBar.sub; barLabel = epBar.label; }   // E1-P2: duyệt = bắt đầu viết ngay, kèm ước tiền
  const approveBlocked = readOnly || noDoc || editing || errored || paidOff || (gate === 'ENDING' && !optionId) || (gate === 'EPISODES' && overlong.length > 0);
  const doApprove = (): void => onApprove(approveChoiceOf(gate, { optionId: optionId ?? undefined, choices, keepOverlong: [] }));
  const keepExtra = gate === 'EPISODES' && overlong.length > 0 && !readOnly
    ? <button type="button" className="btn" disabled={paidOff || noDoc || editing || errored} onClick={() => onApprove(approveChoiceOf('EPISODES', { keepOverlong: overlong }))}>{epBar.keepLabel(overlong.length)}</button> : null;

  return (
    <>
      {readOnly ? <Alert kind="ok">Bạn đã duyệt cổng này; máy không tự đổi quyết định đã chốt. <button type="button" className="btn" onClick={onBackToCurrent}>Về công việc hiện tại</button></Alert> : null}
      {revised && !readOnly ? <Alert kind="ok">Đã sửa theo yêu cầu. Đọc bản mới rồi duyệt.</Alert> : null}
      {editing && !readOnly ? <Alert kind="blue">Máy đang sửa theo lời bạn. Bạn có thể rời trang; kết quả sẽ được lưu.</Alert> : null}
      {errored && !readOnly ? (
        <Alert kind={errCopy.button === 'raise-cap' ? '' : 'bad'}>{errCopy.text}{' '}
          {errCopy.moneyLine ? <><br />{errCopy.moneyLine}. </> : null}
          <button type="button" className="btn" disabled={busy || errCopy.disabled} onClick={errCopy.button === 'raise-cap' ? onRaiseCap : onResume}>{errCopy.label}</button>
        </Alert>
      ) : null}
      {gate === 'EPISODES' && overlong.length > 0 && !readOnly && !noDoc ? <Alert><b>{overlong.length} tập ước vượt hoặc băng qua trần {String(capSec / 60).replace('.', ',')} phút.</b> Chưa đạt; máy không sửa nhãn thời lượng hay cắt cao trào.{' '}<button type="button" className="btn primary" disabled={paidOff} onClick={onResplit}>Máy chia lại / nén {overlong.length > 1 ? 'cả hai tập' : 'tập này'}</button></Alert> : null}
      {keepState && !readOnly ? <Alert kind="blue">Nguồn đã đủ ở cả ba nhóm; không có thay đổi nào cần duyệt từng dòng.</Alert> : null}
      <article className={`panel ${readOnly ? 'archive' : ''}`}>
        <div className="row between dochead">
          <div><span className="eyebrow">Quyết định {idx + 1} / 4</span><h2>{GATE_TITLE[gate]}</h2></div>
          <span className={`chip ${readOnly ? 'ok' : 'blue'}`}>{readOnly ? 'Đã duyệt' : 'Chờ bạn'}</span>
        </div>
        <div className={`reader ${isLongContent(gate, data) ? 'long' : ''}`} tabIndex={0}>{body}</div>
        {showInstructionBox(readOnly) ? (
          <InstructionBox ref={instrRef} value={instruction} onChange={setInstruction} disabled={noDoc || editing} submitDisabled={paidOff}
            busyLabel={editing ? 'Đang sửa…' : undefined} onSubmit={() => onRewrite(instruction, gate === 'CHANGES' ? choices : undefined)} />
        ) : null}
      </article>
      {readOnly ? null : <DecisionBar title={barTitle} sub={barSub} label={barLabel} disabled={approveBlocked} onClick={doApprove} extra={keepExtra} />}
    </>
  );
};
